import { useTranslation } from 'react-i18next';
import { Users, HardDrive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';

interface UsageData {
  users: {
    included: number;
    used: number;
  };
  storage: {
    includedGb: number;
    usedGb: number;
  };
}

interface UsageDisplayProps {
  usage: UsageData | null;
}

export function UsageDisplay({ usage }: UsageDisplayProps) {
  const { t } = useTranslation();

  if (!usage) return null;

  const userPercentage =
    usage.users.included === -1 ? 0 : (usage.users.used / usage.users.included) * 100;
  const storagePercentage = (usage.storage.usedGb / usage.storage.includedGb) * 100;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('billing.usage')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{t('billing.usersUsed')}</span>
            </div>
            <span>
              {usage.users.used} /{' '}
              {usage.users.included === -1 ? t('billing.unlimited') : usage.users.included}
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                userPercentage > 90
                  ? 'bg-red-500'
                  : userPercentage > 70
                    ? 'bg-yellow-500'
                    : 'bg-primary'
              }`}
              style={{ width: `${Math.min(userPercentage, 100)}%` }}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              <span>{t('billing.storageUsed')}</span>
            </div>
            <span>
              {usage.storage.usedGb.toFixed(2)} GB / {usage.storage.includedGb} GB
            </span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                storagePercentage > 90
                  ? 'bg-red-500'
                  : storagePercentage > 70
                    ? 'bg-yellow-500'
                    : 'bg-primary'
              }`}
              style={{ width: `${Math.min(storagePercentage, 100)}%` }}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
